import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CreatePerformancePeriod,
    CreatePerformancePeriodFailure,
    CreatePerformancePeriodSuccess,
    DeletePerformancePeriod,
    DeletePerformancePeriodFailure,
    DeletePerformancePeriodSuccess,
    FetchPerformancePeriod,
    FetchPerformancePeriodFailure,
    FetchPerformancePeriodSuccess,
    SetPerformancePeriod,
    SetPerformancePeriods,
    UnsetPerformancePeriod,
    UpdatePerformancePeriod,
    UpdatePerformancePeriodFailure,
    UpdatePerformancePeriodSuccess,
} from '../actions';
import { AddPerformancePeriods } from '../actions/add-entities.action';
import { PerformancePeriodApiService } from '../services/performance-period.api-service';
import { PerformancePeriod } from './performance-period.model';
import { PerformancePeriodStateModel } from './performance-period.state-model';
import { HttpErrorResponse } from '@angular/common/http';

@State<PerformancePeriodStateModel>({
    name: 'performancePeriod',
    defaults: new PerformancePeriodStateModel(),
})
@Injectable()
export class PerformancePeriodState extends EntityState<PerformancePeriod> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: PerformancePeriodStateModel, id: string = state.item): PerformancePeriod {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: PerformancePeriodStateModel): PerformancePeriod[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(PerformancePeriodState.sortProperties));

        return toReturn;
    }

    constructor(private _performancePeriodService: PerformancePeriodApiService) {
        super(_performancePeriodService, 0);

        this._propstoSearch = PerformancePeriod.props_to_search;
    }

    @Action(AddPerformancePeriods)
    addEntities(ctx: StateContext<PerformancePeriodStateModel>, action: AddPerformancePeriods): void {
        this._addEntities(ctx, action);
    }

    @Action(SetPerformancePeriod)
    setEntity(ctx: StateContext<PerformancePeriodStateModel>, action: SetPerformancePeriod): void {
        this._setEntity(ctx, action, AddPerformancePeriods);
    }

    @Action(UnsetPerformancePeriod)
    unsetEntity(ctx: StateContext<PerformancePeriodStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreatePerformancePeriod)
    createPerformancePeriod(ctx: StateContext<PerformancePeriodStateModel>, { payload }: CreatePerformancePeriod): void {
        this._performancePeriodService.create(payload).subscribe(
            (entity) => {
                this._addEntities(ctx, { payload: [entity] });
                ctx.dispatch(new CreatePerformancePeriodSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreatePerformancePeriodFailure({ response }));
            }
        );
    }

    @Action(UpdatePerformancePeriod)
    updateEntity(ctx: StateContext<PerformancePeriodStateModel>, action: UpdatePerformancePeriod): void {
        this._updateEntity(ctx, action, UpdatePerformancePeriodSuccess, UpdatePerformancePeriodFailure, AddPerformancePeriods).then(() => {});
    }

    @Action(DeletePerformancePeriod)
    deleteEntity(ctx: StateContext<PerformancePeriodStateModel>, action: DeletePerformancePeriod): void {
        this._deleteEntity(ctx, action, DeletePerformancePeriodSuccess, DeletePerformancePeriodFailure).then(() => {});
    }

    @Action(FetchPerformancePeriod)
    fetchEntity(ctx: StateContext<PerformancePeriodStateModel>, action: FetchPerformancePeriod): void {
        this._fetchEntity(ctx, action, FetchPerformancePeriodSuccess, FetchPerformancePeriodFailure, AddPerformancePeriods, SetPerformancePeriod).then(() => {});
    }

    @Action(FetchPerformancePeriodSuccess)
    fetchEntitySuccess(ctx: StateContext<PerformancePeriodStateModel>, action: FetchPerformancePeriodSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchPerformancePeriodFailure)
    fetchEntityFailure(ctx: StateContext<PerformancePeriodStateModel>, action: FetchPerformancePeriodFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetPerformancePeriods)
    setEntities(ctx: StateContext<PerformancePeriodStateModel>, action: SetPerformancePeriods): void {
        this._setEntities(ctx, action);
    }
}
